<template>
  <div class="flex flex-col">
    <label v-if="label" :for="id" class="block text-sm font-medium leading-6 text-neutral-900 dark:text-white mb-2">
      {{ label }}
      <small v-if="labelNote" class="font-normal">{{ labelNote }}</small>
      <slot name="labelSuffix" />
    </label>
    <div v-if="description" class="mb-2 text-sm text-neutral-500 dark:text-neutral-400">
      {{ description }}
    </div>
    <div class="flex rounded-md shadow-xs min-w-12">
      <component
        :is="prefixButton ? 'NtButton' : 'span'"
        v-if="$slots.prefix"
        :level="prefixButton ? 'primary' : null"
        :disabled="disabled"
        :class="
          prefixButton
            ? 'rounded-r-none truncate shrink-0'
            : 'inline-flex items-center rounded-l-md border border-r-0 border-neutral-300 dark:border-neutral-700 px-3 text-neutral-500 sm:text-sm shrink-0'
        "
        @click="$emit('prefix-click')">
        <slot name="prefix"></slot>
      </component>

      <div class="rounded-md relative grow">
        <input
          v-if="component === 'input'"
          :type="type"
          :id="id"
          v-model.trim="content"
          class="block w-full rounded-md border-0 py-1.5 ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
          :class="[
            classes,
            error ? 'pr-10' : null,
            $slots.prefix ? 'rounded-l-none' : null,
            $slots.suffix ? 'rounded-r-none' : null,
            inputClass
          ]"
          :placeholder="placeholder"
          :aria-describedby="hintId"
          :aria-invalid="error ? 'true' : 'false'"
          :disabled="disabled || formDisabled"
          :readonly="readonly"
          :autofocus="autofocus"
          :min="min"
          :step="step"
          :max="max"
          :autocomplete="type === 'password' ? 'new-password' : 'off'"
          @change="change"
          @paste="paste"
          @wheel="wheel"
          @blur="$emit('blur')"
          @focus="$emit('focus')"
          @keypress.enter="$emit('enter')"
          @keydown="$emit('keydown', $event)" />

        <textarea
          v-else-if="component === 'textarea'"
          :id="id"
          :rows="rows"
          v-model.trim="content"
          class="block w-full rounded-md border-0 py-1.5 ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
          :class="[
            classes,
            error ? 'pr-10' : null,
            $slots.prefix ? 'rounded-l-none' : null,
            $slots.suffix ? 'rounded-r-none' : null,
            autoGrow ? 'field-sizing-content overflow-y-auto' : null
          ]"
          :style="autoGrowStyles"
          :placeholder="placeholder"
          :aria-describedby="hintId"
          :aria-invalid="error ? 'true' : 'false'"
          :disabled="disabled || formDisabled"
          :readonly="readonly"
          :autofocus="autofocus"
          @change="change"
          @paste="paste"
          @blur="$emit('blur')"
          @focus="$emit('focus')"
          @keypress.enter="$emit('enter')"
          @keydown="$emit('keydown', $event)"></textarea>
        <div v-if="error || forceError" class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <i class="fa-sharp fa-exclamation-circle text-red-500" aria-hidden="true"></i>
        </div>
      </div>

      <component
        :is="suffixButton ? 'NtButton' : 'span'"
        v-if="$slots.suffix"
        :level="suffixButton ? 'primary' : null"
        :disabled="disabled"
        :class="
          suffixButton
            ? 'rounded-l-none truncate shrink-0'
            : 'inline-flex items-center rounded-r-md border border-l-0 border-neutral-300 dark:border-neutral-700 px-3 text-neutral-500 sm:text-sm shrink-0'
        "
        @click="$emit('suffix-click')">
        <slot name="suffix"></slot>
      </component>
    </div>
    <p v-if="error" class="mt-2 text-sm text-red-600" :id="hintId">
      {{ error }}
    </p>
    <p v-else-if="hint" class="mt-2 text-sm text-neutral-500 dark:text-neutral-400" :id="hintId">
      {{ hint }}
    </p>
  </div>
</template>

<script>
import Str from 'laravel-js-str'
import NtButton from '../general/NtButton.vue'

export default {
  components: { NtButton },
  emits: [
    'update:model-value',
    'change',
    'paste',
    'prefix-click',
    'suffix-click',
    'enter',
    'blur-sm',
    'blur',
    'focus',
    'keydown'
  ],
  inject: {
    form: {
      default: null
    },
    formDisabled: {
      default: false
    }
  },
  props: {
    modelModifiers: {
      default: () => ({})
    },
    type: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      default: null
    },
    labelNote: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    hint: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    modelValue: {
      default: null
    },
    prefixButton: {
      type: Boolean,
      default: false
    },
    suffixButton: {
      type: Boolean,
      default: false
    },
    rows: {
      type: Number,
      default: 1
    },
    // Grows a textarea with its content, from `rows` up to `maxRows`, then scrolls.
    autoGrow: {
      type: Boolean,
      default: false
    },
    maxRows: {
      type: Number,
      default: 6
    },
    min: {
      type: Number,
      default: null
    },
    step: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    noDebounce: {
      type: Boolean,
      default: false
    },
    forceError: {
      type: Boolean,
      default: false
    },
    decimals: {
      type: Number,
      default: null
    },
    highlight: {
      type: Boolean,
      default: false
    },
    inputClass: {
      type: [null, String],
      default: null
    },
    formatNumber: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      debounceTimer: null,
      newValue: this.modelValue
    }
  },
  computed: {
    component() {
      return this.rows > 1 ? 'textarea' : 'input'
    },
    autoGrowStyles() {
      if (!this.autoGrow) {
        return null
      }

      // `field-sizing: content` ignores the rows attribute, so the bounds are set here
      // instead. 1lh is one line of text at whatever line-height ends up applying, and
      // the 0.75rem accounts for the py-1.5 padding inside the border box.
      return {
        minHeight: `calc(${this.rows}lh + 0.75rem)`,
        maxHeight: `calc(${this.maxRows}lh + 0.75rem)`
      }
    },
    id() {
      return Str.random()
    },
    hintId() {
      return this.hint || this.error ? `${this.id}Hint` : null
    },
    classes() {
      if (this.error || this.forceError) {
        return 'bg-red-100 dark:bg-red-950 text-red-900 dark:text-neutral-100 ring-red-300 dark:ring-red-500 placeholder:text-red-300 focus:ring-red-500'
      }

      if (this.highlight && !this.disabled && !this.formDisabled) {
        return 'bg-yellow-100 dark:bg-yellow-950 text-yellow-900 dark:text-neutral-100 ring-yellow-300 dark:ring-yellow-500 placeholder:text-yellow-300 focus:ring-yellow-500'
      }

      return 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 ring-neutral-300 dark:ring-neutral-600 placeholder:text-neutral-300 dark:placeholder:text-neutral-500 focus:ring-accent-600 dark:focus:ring-accent-500 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500 disabled:ring-neutral-200'
    },
    error() {
      if (!this.form?.errors || !this.name) {
        return null
      }
      return this.form.errors[this.name]
    },
    content: {
      get() {
        // For normal decimal formatting
        let val = this.modelValue
        if (this.decimals && val !== null && !isNaN(val)) {
          val = parseFloat(val || 0).toFixed(this.decimals)
        }
        // Format with commas if enabled
        if (this.formatNumber && val !== null && val !== '' && !isNaN(val)) {
          val = parseFloat(val).toLocaleString('en-GB') // 1,234,567.89 style
        }
        return val
      },
      set(val) {
        clearTimeout(this.debounceTimer)
        // Remove currency symbols and commas for numeric inputs
        if (typeof val === 'string' && !this.formatNumber && (this.decimals || this.modelModifiers.numeric)) {
          val = val.replace(/[£,]/g, '')
        }
        if (this.formatNumber && typeof val === 'string') {
          val = val.replace(/[^\d.-]/g, '')
        }
        if (this.modelModifiers.uppercase) {
          val = val?.toUpperCase() || null
        }
        if (this.modelModifiers.numeric) {
          const modified = parseFloat(val?.toString().replace(/[^\d.]/g, ''))
          val = isNaN(modified) ? null : modified
        }
        if (this.modelModifiers.video) {
          val = this.$helpers.sanitiseVideoUrl(val)
        }
        this.newValue = val
        this.debounceTimer = setTimeout(() => this.emitValue(), this.noDebounce ? 0 : 2000)
      }
    }
  },
  methods: {
    emitValue() {
      clearTimeout(this.debounceTimer)
      this.$emit('update:model-value', this.newValue)
    },
    change() {
      this.emitValue()
      this.$emit('change', this.content)
      if (!this.form || !this.name) {
        return
      }
      this.form.validate(this.name)
    },
    paste(e) {
      // A paste event made by a script, such as a password manager filling a field in,
      // carries no clipboard.
      if (!e.clipboardData) {
        return
      }
      this.$emit('paste', e.clipboardData.getData('text/plain'))
    },
    wheel(e) {
      // Chrome increments a focused <input type="number"> when the mouse wheel is
      // scrolled over it, silently corrupting values (a serious problem for MSF
      // reports). Blurring the field on wheel removes focus so the native increment
      // no longer applies, while still letting the page scroll normally (which a
      // preventDefault would block). Only number inputs are affected.
      if (this.type === 'number' && document.activeElement === e.target) {
        e.target.blur()
      }
    }
  }
}
</script>
